import React, { useId, useRef, useState } from 'react';
import {
  LayoutChangeEvent,
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import VXLTextInput, {
  VXLTextInputProps,
} from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import theme from 'shared/styles/theme';

import {
  getHighlightedLabelParts,
  getNextOptionIndex,
} from './VXLSearchField.helpers';

const MENU_OPTION_MIN_HEIGHT = 54;
const MENU_MAX_HEIGHT = MENU_OPTION_MIN_HEIGHT * 4.5;
const MENU_VERTICAL_OFFSET = theme.spacing.xs;
const MENU_OVERLAY_SPACE = MENU_MAX_HEIGHT + MENU_VERTICAL_OFFSET;
const CLEAR_SEARCH_ACCESSIBILITY_LABEL = 'Clear search';
const EMPTY_RESULTS_TEXT = 'No results found. Try updating your search.';
const LOADING_RESULTS_ACCESSIBILITY_LABEL = 'Loading results';
const SEARCH_ICON_ACCESSIBILITY_LABEL = 'Search';

export interface VXLSearchFieldOption<T> {
  key: string;
  label: string;
  value: T;
}

export interface VXLSearchFieldProps<T> extends Omit<
  VXLTextInputProps,
  | 'caretHidden'
  | 'leftIcon'
  | 'multiline'
  | 'onChangeText'
  | 'readOnly'
  | 'rightIcon'
  | 'showSoftInputOnFocus'
  | 'value'
> {
  /** Whether an async option fetch is in progress. */
  isLoading?: boolean;
  /** Keeps the menu open after selecting options that load a follow-up result set. */
  keepMenuOpenOnSelect?: (option: T) => boolean;
  /** Called when the user changes the search text. */
  onChangeText: (value: string) => void;
  /** Called when the user selects an autocomplete option. */
  onSelectOption: (option: T) => void;
  /** Options to render. Consumers own filtering and async search. */
  options: VXLSearchFieldOption<T>[];
  /** Current search text. The search field is fully controlled. */
  value: string;
}

function VXLSearchField<T>({
  disabled,
  editable,
  isLoading = false,
  keepMenuOpenOnSelect,
  onBlur,
  onChangeText,
  onFocus,
  onKeyPress,
  onSelectOption,
  options,
  value,
  ...inputProps
}: VXLSearchFieldProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeOptionKey, setActiveOptionKey] = useState<string | null>(null);
  const [fieldHeight, setFieldHeight] = useState(0);
  const ignoreNextBlurRef = useRef(false);
  const inputRef = useRef<TextInput>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const menuId = `${useId()}-menu`;
  const isDisabled = disabled === true || editable === false;
  const hasOptions = options.length > 0;
  const shouldShowLoading = isLoading && !hasOptions;
  const shouldShowEmpty = !isLoading && !hasOptions && value.trim().length > 0;
  const showMenu =
    isOpen &&
    !isDisabled &&
    (hasOptions || shouldShowLoading || shouldShowEmpty);

  const scrollOptionIntoView = (optionIndex: number) => {
    scrollViewRef.current?.scrollTo({
      animated: true,
      y: optionIndex * MENU_OPTION_MIN_HEIGHT,
    });
  };

  const handleFieldLayout = (event: LayoutChangeEvent) => {
    setFieldHeight(event.nativeEvent.layout.height);
  };

  const handleCloseMenu = () => {
    setIsOpen(false);
    setActiveOptionKey(null);
    ignoreNextBlurRef.current = false;
  };

  const handleOpenMenu = () => {
    if (!isDisabled) {
      setIsOpen(true);
    }
  };

  const handleFocusOpen = () => {
    handleOpenMenu();
  };

  const handleSelectOption = (option: VXLSearchFieldOption<T>) => {
    const shouldKeepOpen = keepMenuOpenOnSelect?.(option.value) === true;

    onSelectOption(option.value);
    setActiveOptionKey(null);

    if (shouldKeepOpen) {
      setIsOpen(true);
      return;
    }

    handleCloseMenu();
    inputRef.current?.blur();
  };

  const handleClear = () => {
    if (isDisabled) {
      return;
    }

    onChangeText('');
    handleCloseMenu();
    inputRef.current?.focus();
  };

  const handleMoveActiveOption = (direction: 1 | -1) => {
    if (!hasOptions) {
      handleOpenMenu();
      return;
    }

    const activeOptionIndex = options.findIndex(
      (option) => option.key === activeOptionKey,
    );
    const nextOptionIndex = getNextOptionIndex({
      activeOptionIndex,
      direction,
      optionCount: options.length,
    });
    const nextOption = options[nextOptionIndex];

    if (nextOption) {
      setActiveOptionKey(nextOption.key);
      handleOpenMenu();
      scrollOptionIntoView(nextOptionIndex);
    }
  };

  const rightIcon: VXLTextInputProps['rightIcon'] =
    value.length > 0 && !isDisabled
      ? {
          iconKey: 'CLOSE',
          iconAccessibilityLabel: CLEAR_SEARCH_ACCESSIBILITY_LABEL,
          onPress: handleClear,
        }
      : undefined;

  const handleOptionPressIn = () => {
    if (Platform.OS === 'web') {
      return;
    }

    ignoreNextBlurRef.current = true;
  };

  const handleOptionPressOut = () => {
    if (Platform.OS === 'web') {
      return;
    }

    ignoreNextBlurRef.current = false;
  };

  const handleOptionPress = (option: VXLSearchFieldOption<T>) => {
    handleSelectOption(option);
  };

  let menuContent: React.ReactNode = null;

  if (hasOptions) {
    menuContent = options.map((option) => {
      const isActive = option.key === activeOptionKey;
      const highlightedParts = getHighlightedLabelParts(option.label, value);

      return (
        <Pressable
          key={option.key}
          role="option"
          accessibilityLabel={option.label}
          onPress={() => handleOptionPress(option)}
          onPressIn={handleOptionPressIn}
          onPressOut={handleOptionPressOut}
          {...(Platform.OS === 'web' && {
            onMouseDown: (event: { preventDefault: () => void }) =>
              event.preventDefault(),
          })}
          style={({ pressed, hovered }) => [
            styles.optionRow,
            !pressed && (hovered || isActive) ? styles.optionRowHovered : null,
            pressed ? styles.optionRowPressed : null,
          ]}
        >
          {({ pressed, hovered }) => {
            const useInverseText = pressed || hovered || isActive;

            return (
              <BodyBaseText
                numberOfLines={0}
                style={[
                  styles.optionText,
                  useInverseText ? styles.optionTextInverse : null,
                ]}
              >
                {highlightedParts.map((part) =>
                  part.isMatch ? (
                    <BodyBaseBoldText
                      key={part.key}
                      numberOfLines={0}
                      style={[
                        styles.optionText,
                        useInverseText ? styles.optionTextInverse : null,
                      ]}
                    >
                      {part.text}
                    </BodyBaseBoldText>
                  ) : (
                    part.text
                  ),
                )}
              </BodyBaseText>
            );
          }}
        </Pressable>
      );
    });
  } else if (shouldShowLoading) {
    menuContent = (
      <View
        role="status"
        accessibilityLabel={LOADING_RESULTS_ACCESSIBILITY_LABEL}
        style={styles.loadingRow}
      >
        <View style={styles.loadingIndicatorContainer}>
          <LoadingSpinner />
        </View>
      </View>
    );
  } else {
    menuContent = (
      <View style={styles.emptyRow} role="status">
        <BodyBaseText numberOfLines={0}>{EMPTY_RESULTS_TEXT}</BodyBaseText>
      </View>
    );
  }

  return (
    <View
      style={[styles.container, showMenu ? styles.containerOpen : null]}
      testID="vxl-search-field-container"
    >
      <View onLayout={handleFieldLayout} testID="vxl-search-field-input-anchor">
        <VXLTextInput
          {...inputProps}
          ref={inputRef}
          value={value}
          disabled={isDisabled}
          editable={isDisabled ? false : editable}
          onBlur={(event) => {
            if (ignoreNextBlurRef.current) {
              ignoreNextBlurRef.current = false;
              setIsOpen(true);
              inputRef.current?.focus();
              return;
            }

            handleCloseMenu();
            onBlur?.(event);
          }}
          onChangeText={(nextValue) => {
            if (isDisabled) {
              return;
            }

            onChangeText(nextValue);

            if (nextValue.length > 0) {
              handleOpenMenu();
            } else {
              handleCloseMenu();
            }
          }}
          onFocus={(event) => {
            handleFocusOpen();
            onFocus?.(event);
          }}
          onKeyPress={(event) => {
            const { key } = event.nativeEvent;

            if (key === 'ArrowDown') {
              handleMoveActiveOption(1);
              return;
            }

            if (key === 'ArrowUp') {
              handleMoveActiveOption(-1);
              return;
            }

            if (key === 'Enter') {
              const selectedOption = options.find(
                (option) => option.key === activeOptionKey,
              );

              if (selectedOption) {
                handleSelectOption(selectedOption);
                return;
              }
            }

            if (key === 'Escape') {
              handleCloseMenu();
              return;
            }

            onKeyPress?.(event);
          }}
          leftIcon={{
            iconKey: 'SEARCH',
            iconAccessibilityLabel: SEARCH_ICON_ACCESSIBILITY_LABEL,
          }}
          rightIcon={rightIcon}
          aria-expanded={showMenu}
          aria-controls={showMenu ? menuId : undefined}
          role="combobox"
          testID={inputProps.testID ?? 'text-input-searchbox'}
        />
      </View>
      {showMenu ? (
        <View
          style={[
            styles.menuAnchor,
            { top: fieldHeight + MENU_VERTICAL_OFFSET },
          ]}
          testID="vxl-search-field-menu-surface"
          {...(Platform.OS === 'web' && {
            onMouseDown: (event: { preventDefault: () => void }) =>
              event.preventDefault(),
          })}
        >
          <ScrollView
            ref={scrollViewRef}
            nativeID={menuId}
            style={styles.menu}
            contentContainerStyle={styles.menuContent}
            keyboardShouldPersistTaps="always"
            nestedScrollEnabled
            showsVerticalScrollIndicator={hasOptions}
          >
            {menuContent}
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    zIndex: 1,
  },
  containerOpen: {
    elevation: 8,
    marginBottom: -MENU_OVERLAY_SPACE,
    paddingBottom: MENU_OVERLAY_SPACE,
    zIndex: 1000,
  },
  emptyRow: {
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  loadingIndicatorContainer: {
    height: 24,
    width: 24,
  },
  loadingRow: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: MENU_OPTION_MIN_HEIGHT,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  menu: {
    backgroundColor: theme.color.background.primary,
    borderColor: theme.color.border.primarySolid,
    borderRadius: 4,
    borderWidth: 1,
    boxShadow:
      '0 4px 36px 0 rgba(0, 0, 0, 0.16), 0 2px 8px 0 rgba(0, 0, 0, 0.12)',
    elevation: 8,
    maxHeight: MENU_MAX_HEIGHT,
    overflow: 'hidden',
  },
  menuContent: {
    backgroundColor: theme.color.background.primary,
  },
  menuAnchor: {
    backgroundColor: theme.color.background.primary,
    left: 0,
    elevation: 8,
    position: 'absolute',
    right: 0,
    zIndex: 1000,
  },
  optionRow: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    borderBottomColor: theme.color.border.primarySolid,
    borderBottomWidth: 1,
    flexDirection: 'row',
    minHeight: MENU_OPTION_MIN_HEIGHT,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  optionRowHovered: {
    backgroundColor: theme.color.interactive.primaryHovered,
  },
  optionRowPressed: {
    backgroundColor: theme.color.interactive.primaryPressed,
  },
  optionText: {
    color: theme.color.text.primary,
    flex: 1,
  },
  optionTextInverse: {
    color: theme.color.text.inverse,
  },
});

export default VXLSearchField;
